// react
import { useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

// material ui
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';

// shared components
import DataGridNoRowsCustomOverlay from '@/components/ui/datagrid/DataGridNoRowsOverlay';

// feature-components
import { isSummaryOutOfBounds } from '@/features/energyxplorer/components/timestamps-summary/timestampsSummaryEvaluation';

// feature-hooks
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';

// components
import { useUrlDateRange } from '@/hooks/useUrlDateRange';
import { formatNumber } from '@/utils/formatters';
import {
  getTranslatedNodeType,
  getTypeColor,
  getTypeIcon,
} from '@/utils/nodeTypeMapper';

import { useTheme } from '@emotion/react';

const InfrastructureNodeDataGrid = ({ energyOverviewDataResult }) => {
  const apiRef = useGridApiRef();
  const { unit } = useEnergyGroupUnit();
  const { t } = useTranslation('energyxplorer-energy-overview-tab');

  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Track expanded groups
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortModel, setSortModel] = useState([
    { field: 'consumption', sort: 'desc' },
  ]);
  const { timeRange } = useUrlDateRange();

  const handleRowToggle = (rowId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Track expanded/collapsed state dynamically
  const handleRowExpansion = (row) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(row)) {
        newSet.delete(row); // Remove if already expanded
      } else {
        newSet.add(row); // Add when expanded
      }
      return newSet;
    });
  };

  // Determine whether "Total" is in the inline group or footer
  const getAggregationPosition = (groupNode) =>
    expandedGroups.has(groupNode.groupingKey) ? 'footer' : 'inline';

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {},
  });

  const columns = [
    {
      field: 'icon',
      headerName: t('data_grid_infrastructure_columns.boards.header'),
      width: 150,
      renderCell: (params) => {
        const row = params.row;
        const isExpanded = expandedRows.has(row.id);
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Tooltip
              title={getTranslatedNodeType(params.value, t)}
              placement="top"
            >
              <Avatar
                variant="round"
                size="small"
                sx={{
                  backgroundColor: getTypeColor(params.value) || '#555',
                  color: '#fff',
                  height: 32,
                  width: 32,
                  fontSize: 16,
                }}
              >
                <i className={`fas ${getTypeIcon(params.value)}`}></i>
              </Avatar>
            </Tooltip>
            {row.hasChildren && (
              <IconButton
                size="small"
                onClick={() => handleRowToggle(row.id)}
                sx={{ ml: 2 }}
              >
                {isExpanded ? (
                  <ExpandLessRoundedIcon />
                ) : (
                  <ExpandMoreRoundedIcon />
                )}
              </IconButton>
            )}
          </Box>
        );
      },
    },
    {
      field: 'name',
      headerName: t('data_grid_infrastructure_columns.name.header'),
      width: 300,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Typography>{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'consumption',
      headerName: t('data_grid_infrastructure_columns.consumption.header'),
      flex: 1,
      minWidth: 150,
      type: 'number',
      groupable: false,
      aggregable: true,
      headerAlign: 'left',
      aggregationFn: (params) => {
        return params.values.reduce((sum, val) => sum + (val || 0), 0);
      },
      valueFormatter: (params) => {
        return params.value != null ? `${params.value} ${unit}` : '-';
      },
      renderCell: (params) => {
        const { value } = params;
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Typography>
              {value != null ? `${formatNumber(value)} ${unit}` : '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'accounted',
      headerName: t('data_grid_infrastructure_columns.breakdown_share.header'),
      flex: 1,
      minWidth: 150,
      type: 'number',
      groupable: false,
      aggregable: false,
      headerAlign: 'left',
      valueFormatter: (params) => {
        return params.value != null ? `${params.value} ${unit}` : '-';
      },
      renderCell: (params) => {
        const { row, value } = params;
        return row.accountedPercentage !== 0 ? (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Typography>
              {value !== null ? `${formatNumber(value)} ${unit}` : '100%'}
            </Typography>
            <CoverageChip
              renderWarning={isSummaryOutOfBounds(
                row.timestampsSummary,
                timeRange,
              )}
              percentage={row.accountedPercentage}
            />
          </Box>
        ) : (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Typography
              color="text.secondary"
              fontStyle="italic"
              fontSize="0.9em"
            >
              {t('nodes.infrastructure_grid.not_subdivided', {
                ns: 'energyxplorer',
              })}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const rows = useMemo(() => {
    if (!energyOverviewDataResult) return [];

    const { MainMeterSetup, DistributionBoardsSummary } =
      energyOverviewDataResult;
    const resultRows = [];

    if (MainMeterSetup === 'main_meter_segregation') {
      // Three-level hierarchy structure:
      // Level 1: MainMeter
      // Level 2: SubMeters (MainDistributionBoard)
      // Level 3: SubDistributionBoards
      DistributionBoardsSummary.forEach(({ MainMeter, SubMeters }) => {
        // Level 1: Add MainMeter as top-level parent
        resultRows.push({
          id: MainMeter.Id,
          parentId: null,
          name: MainMeter.Name,
          icon: 'Hovedmåler', // or appropriate icon type
          consumption: MainMeter.TotalEnergyUsage,
          accounted: MainMeter.ChildEnergyUsage,
          accountedPercentage: MainMeter.ChildEnergyUsagePercentage,
          unaccounted: MainMeter.NotAnalysedEnergyUsage,
          unaccountedPercentage: MainMeter.NotAnalysedEnergyUsagePercentage,
          hasChildren: (SubMeters || []).length > 0,
          timestampsSummary: MainMeter.TimestampsSummary,
          level: 0,
        });

        // Level 2: Add SubMeters (MainDistributionBoards) as children of MainMeter
        (SubMeters || []).forEach(
          ({ MainDistributionBoard, SubDistributionBoards }) => {
            resultRows.push({
              id: MainDistributionBoard.Id,
              parentId: MainMeter.Id,
              name: MainDistributionBoard.Name,
              icon: 'Tavle',
              consumption: MainDistributionBoard.TotalEnergyUsage,
              accounted: MainDistributionBoard.ChildEnergyUsage,
              accountedPercentage:
                MainDistributionBoard.ChildEnergyUsagePercentage,
              unaccounted: MainDistributionBoard.NotAnalysedEnergyUsage,
              unaccountedPercentage:
                MainDistributionBoard.NotAnalysedEnergyUsagePercentage,
              hasChildren: (SubDistributionBoards || []).length > 0,
              timestampsSummary: MainDistributionBoard.TimestampsSummary,
              level: 1,
            });

            // Level 3: Add SubDistributionBoards as children of MainDistributionBoard
            (SubDistributionBoards || []).forEach((sub) => {
              resultRows.push({
                id: sub.Id,
                parentId: MainDistributionBoard.Id,
                name: sub.Name,
                icon: 'Undertavle',
                consumption: sub.TotalEnergyUsage,
                accounted: sub.ChildEnergyUsage,
                accountedPercentage: sub.ChildEnergyUsagePercentage,
                unaccounted: sub.NotAnalysedEnergyUsage,
                unaccountedPercentage: sub.NotAnalysedEnergyUsagePercentage,
                hasChildren: false,
                timestampsSummary: sub.TimestampsSummary,
                level: 2,
              });
            });
          },
        );
      });
    } else {
      // Two-level hierarchy (virtual_main_meter / data_main_meter):
      // Level 1: MainDistributionBoard
      // Level 2: SubDistributionBoards
      DistributionBoardsSummary.forEach(
        ({ MainDistributionBoard, SubDistributionBoards }) => {
          // Level 1: Main distribution board
          resultRows.push({
            id: MainDistributionBoard.Id,
            parentId: null,
            name: MainDistributionBoard.Name,
            icon: 'Tavle',
            consumption: MainDistributionBoard.TotalEnergyUsage,
            accounted: MainDistributionBoard.ChildEnergyUsage,
            accountedPercentage:
              MainDistributionBoard.ChildEnergyUsagePercentage,
            unaccounted: MainDistributionBoard.NotAnalysedEnergyUsage,
            unaccountedPercentage:
              MainDistributionBoard.NotAnalysedEnergyUsagePercentage,
            hasChildren: (SubDistributionBoards || []).length > 0,
            timestampsSummary: MainDistributionBoard.TimestampsSummary,
            level: 0,
          });

          // Level 2: Sub distribution boards
          (SubDistributionBoards || []).forEach((sub) => {
            resultRows.push({
              id: sub.Id,
              parentId: MainDistributionBoard.Id,
              name: sub.Name,
              icon: 'Undertavle',
              consumption: sub.TotalEnergyUsage,
              accounted: sub.ChildEnergyUsage,
              accountedPercentage: sub.ChildEnergyUsagePercentage,
              unaccounted: sub.NotAnalysedEnergyUsage,
              unaccountedPercentage: sub.NotAnalysedEnergyUsagePercentage,
              hasChildren: false,
              timestampsSummary: sub.TimestampsSummary,
              level: 1,
            });
          });
        },
      );
    }

    return resultRows;
  }, [energyOverviewDataResult]);

  const sortedParentRows = useMemo(() => {
    const parents = rows.filter((row) => row.parentId === null);

    if (sortModel.length === 0) return parents;

    const { field, sort } = sortModel[0];
    return [...parents].sort((a, b) => {
      const aValue = a[field] ?? '';
      const bValue = b[field] ?? '';

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sort === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        return sort === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }
    });
  }, [rows, sortModel]);

  const visibleRows = useMemo(() => {
    const result = [];

    // Helper function to recursively add children
    const addRowAndChildren = (row, depth = 0) => {
      result.push(row);

      if (expandedRows.has(row.id)) {
        const children = rows.filter((r) => r.parentId === row.id);
        children.forEach((child) => addRowAndChildren(child, depth + 1));
      }
    };

    // Start with top-level parents (parentId === null)
    sortedParentRows.forEach((parent) => {
      addRowAndChildren(parent, 0);
    });

    return result;
  }, [sortedParentRows, rows, expandedRows]);

  return (
    <DataGridPremium
      apiRef={apiRef}
      rows={visibleRows}
      columns={columns}
      hideFooter
      density="standard"
      sortingMode="server"
      getAggregationPosition={(model) => getAggregationPosition(model)} // Dynamically place Total
      onRowExpansionModelChange={(model) => handleRowExpansion(model)}
      initialState={initialState}
      disableRowSelectionOnClick
      groupingColDef={{ headerName: 'Group', width: 120 }}
      sortModel={sortModel}
      onSortModelChange={(model) => setSortModel(model)}
      getRowClassName={(params) => {
        if (params.row.level === 0) return 'level-0-row';
        if (params.row.level === 1) return 'level-1-row';
        if (params.row.level === 2) return 'level-2-row';
        return params.row.parentId ? 'sub-row' : 'parent-row';
      }}
      aggregationFunctions={{
        ...GRID_AGGREGATION_FUNCTIONS,
      }}
      slots={{
        noRowsOverlay: DataGridNoRowsCustomOverlay,
      }}
      slotProps={{
        noRowsOverlay: {
          rows: t('data_grid_infrastructure_columns.no_rows_overlay'),
          isLoading: false,
        },
      }}
    />
  );
};

export default InfrastructureNodeDataGrid;

const CoverageChip = ({ percentage, renderWarning }) => {
  const value = parseFloat(percentage.toFixed(1));
  const { t } = useTranslation('energyxplorer-energy-overview-tab');
  return (
    <>
      {renderWarning ? (
        <Tooltip
          placement="top"
          title={t(
            'data_grid_infrastructure_columns.timestamp_summary_warning',
          )}
        >
          <Box>
            <HashedChip label={`${value}%`} />
          </Box>
        </Tooltip>
      ) : (
        <Chip
          label={`${value}%`}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 'bold',
            ml: 1,
            minWidth: 60,
            justifyContent: 'center',
          }}
        />
      )}
    </>
  );
};

function HashedChip({ label = '0' }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const color = isDarkMode ? 'gray' : 'lightgray';

  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 'bold',
        color: isDarkMode ? 'white' : 'black',
        backgroundImage: `repeating-linear-gradient(
          45deg,
          ${color},
          ${color} 6px,
          transparent 6px,
          transparent 12px
        )`,
        backgroundColor: `${color}22`,
        opacity: 0.8,
        ml: 1,
        border: `1px solid ${color}`,
      }}
    />
  );
}
